<script setup>
import { useI18n } from 'vue-i18n';

const props = defineProps({
  quickReplies: { type: Array, default: () => [] },
  ctaButtons: { type: Array, default: () => [] },
  canAddQuickReply: { type: Boolean, default: true },
  canAddCta: { type: Boolean, default: true },
  fieldErrors: { type: Object, default: () => ({}) },
});

const emit = defineEmits([
  'add-quick-reply',
  'remove-quick-reply',
  'update:quickReplies',
  'add-cta',
  'remove-cta',
  'update:ctaButtons',
]);

const { t } = useI18n();

const updateQuickReply = (i, val) => {
  const arr = [...props.quickReplies];
  arr[i] = val;
  emit('update:quickReplies', arr);
};

const updateCtaField = (i, field, val) => {
  const arr = props.ctaButtons.map((btn, idx) =>
    idx === i ? { ...btn, [field]: val } : btn
  );
  emit('update:ctaButtons', arr);
};
</script>

<template>
  <div class="space-y-5">
    <!-- Quick Reply Buttons -->
    <div>
      <div class="flex items-center justify-between mb-2">
        <span class="text-xs font-medium text-n-slate-11">
          {{ t('INBOX_MGMT.TEMPLATES.CREATE.QUICK_REPLIES') }}
          {{
            t('INBOX_MGMT.TEMPLATES.CREATE.QUICK_REPLIES_COUNT', {
              count: quickReplies.length,
            })
          }}
        </span>
        <button
          type="button"
          class="h-7 px-2 rounded-md text-xs bg-n-slate-3 text-n-slate-12 hover:bg-n-slate-4 disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="!canAddQuickReply"
          @click="emit('add-quick-reply')"
        >
          {{ t('INBOX_MGMT.TEMPLATES.CREATE.ADD_QUICK_REPLY') }}
        </button>
      </div>
      <div class="space-y-2">
        <div
          v-for="(qr, i) in quickReplies"
          :key="`qr-${i}`"
          class="flex items-center gap-2"
        >
          <input
            :value="qr"
            type="text"
            class="flex-1 h-9 px-3 rounded-lg outline outline-1 outline-n-weak bg-transparent text-sm text-n-slate-12 focus:outline-n-brand"
            :placeholder="t('INBOX_MGMT.TEMPLATES.CREATE.QUICK_REPLY_LABEL_PH')"
            @input="updateQuickReply(i, $event.target.value)"
          />
          <button
            type="button"
            class="h-8 px-2 rounded-md text-xs text-n-ruby-9 hover:bg-n-ruby-3"
            @click="emit('remove-quick-reply', i)"
          >
            {{ t('INBOX_MGMT.TEMPLATES.CREATE.REMOVE') }}
          </button>
        </div>
      </div>
      <p v-if="!canAddQuickReply" class="text-[11px] text-n-slate-10 mt-1">
        {{ t('INBOX_MGMT.TEMPLATES.CREATE.MAX_QUICK_REPLIES') }}
      </p>
    </div>

    <!-- CTA Buttons -->
    <div>
      <div class="flex items-center justify-between mb-2">
        <span class="text-xs font-medium text-n-slate-11">
          {{ t('INBOX_MGMT.TEMPLATES.CREATE.CTA_BUTTONS') }}
          {{
            t('INBOX_MGMT.TEMPLATES.CREATE.CTA_BUTTONS_COUNT', {
              count: ctaButtons.length,
            })
          }}
        </span>
        <button
          type="button"
          class="h-7 px-2 rounded-md text-xs bg-n-slate-3 text-n-slate-12 hover:bg-n-slate-4 disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="!canAddCta"
          @click="emit('add-cta')"
        >
          {{ t('INBOX_MGMT.TEMPLATES.CREATE.ADD_CTA') }}
        </button>
      </div>
      <div class="space-y-3">
        <div
          v-for="(btn, i) in ctaButtons"
          :key="`cta-${i}`"
          class="space-y-2 p-3 rounded-lg bg-n-slate-2"
        >
          <div class="flex items-center gap-2">
            <select
              :value="btn.type"
              class="flex-1 min-w-0 h-9 px-2 rounded-lg outline outline-1 outline-n-weak bg-n-background text-sm text-n-slate-12 focus:outline-n-brand"
              @change="updateCtaField(i, 'type', $event.target.value)"
            >
              <option value="URL">
                {{ t('INBOX_MGMT.TEMPLATES.CREATE.URL') }}
              </option>
              <option value="PHONE_NUMBER">
                {{ t('INBOX_MGMT.TEMPLATES.CREATE.PHONE') }}
              </option>
            </select>
            <input
              :value="btn.label"
              type="text"
              class="flex-1 min-w-0 h-9 px-3 rounded-lg outline outline-1 bg-transparent text-sm text-n-slate-12 focus:outline-n-brand"
              :class="
                !btn.label?.trim() && fieldErrors.buttons
                  ? 'outline-n-ruby-6'
                  : 'outline-n-weak'
              "
              :placeholder="t('INBOX_MGMT.TEMPLATES.CREATE.CTA_LABEL_PH')"
              @input="updateCtaField(i, 'label', $event.target.value)"
            />
            <button
              type="button"
              class="h-8 px-2 rounded-md text-xs text-n-ruby-9 hover:bg-n-ruby-3 shrink-0"
              @click="emit('remove-cta', i)"
            >
              {{ t('INBOX_MGMT.TEMPLATES.CREATE.REMOVE') }}
            </button>
          </div>
          <input
            :value="btn.value"
            type="text"
            class="w-full h-9 px-3 rounded-lg outline outline-1 outline-n-weak bg-transparent text-sm text-n-slate-12 focus:outline-n-brand"
            :placeholder="
              btn.type === 'URL'
                ? t('INBOX_MGMT.TEMPLATES.CREATE.CTA_URL_PH')
                : t('INBOX_MGMT.TEMPLATES.CREATE.CTA_PHONE_PH')
            "
            @input="updateCtaField(i, 'value', $event.target.value)"
          />
        </div>
      </div>
      <p v-if="!canAddCta" class="text-[11px] text-n-slate-10 mt-1">
        {{ t('INBOX_MGMT.TEMPLATES.CREATE.MAX_CTA_BUTTONS') }}
      </p>
      <p v-if="fieldErrors.buttons" class="text-[11px] text-n-ruby-9 mt-1">
        {{ fieldErrors.buttons }}
      </p>
    </div>
  </div>
</template>
